import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  FrontClearanceDeletePayload,
  FrontClearanceFilterEnum,
  FrontClearanceFilterOption,
  FrontClearanceFilterOptionsResponse,
  FrontClearanceForm,
  FrontClearanceFormDropDownKey,
  FrontClearanceListResponse,
  FrontClearanceUpdatePayload,
} from "../types";
import {
  selectFrontClearanceForm,
  selectFrontClearanceFormDropDowns,
  selectFrontClearancePage,
  selectFrontClearancePerPage,
} from "./selector";
import { frontClearanceActions } from "./slice";

let isFetchingList = false;

const fetchingFormDropDowns: Record<FrontClearanceFormDropDownKey, boolean> = {
  manufacturers: false,
  superModels: false,
};

export function* handleGetFrontClearanceList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectFrontClearancePage);
    const perPage: number = yield select(selectFrontClearancePerPage);

    const url = `${API_URL}/front-clearance/list?page=${page}&perPage=${perPage}`;

    const response: FrontClearanceListResponse = yield call(request, url, {
      method: "GET",
    });

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(frontClearanceActions.getFrontClearanceListSuccess(payload));
    } else {
      yield put(frontClearanceActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectFrontClearancePage);

    if (page === 1) {
      yield put(frontClearanceActions.getFrontClearanceListError(message));
    } else {
      yield put(frontClearanceActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddFrontClearance() {
  const form: FrontClearanceForm = yield select(selectFrontClearanceForm);

  try {
    yield call(request, `${API_URL}/front-clearance`, {
      method: "POST",
      body: JSON.stringify({
        superModel: Number(form.masterModel),
        year: Number(form.year),
        cabinType: form.driverCabin.trim(),
        structureType: Number(form.bodyType),
        normalFrontSpace: Number(form.optimalDistance),
        minFrontSpace: Number(form.minimalDistance),
        manufacturer: Number(form.manufacturer),
        model: Number(form.masterModel),
        remarks: form.note.trim(),
      }),
    });

    yield put(frontClearanceActions.addFrontClearanceSuccess());
    yield put(frontClearanceActions.getFrontClearanceList());
  } catch (error) {
    yield put(
      frontClearanceActions.addFrontClearanceError((error as Error).message)
    );
  }
}

export function* handleUpdateFrontClearance(
  action: PayloadAction<FrontClearanceUpdatePayload>,
) {
  const {
    superModel,
    year,
    cabinType,
    structureType,
    normalFrontSpace,
    minFrontSpace,
    manufacturer,
    model,
    remarks,
  } = action.payload;

  try {
    yield call(
      request,
      `${API_URL}/front-clearance?superModel=${superModel}&year=${year}&cabinType=${encodeURIComponent(
        cabinType,
      )}&structureType=${structureType}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          normalFrontSpace: Number(normalFrontSpace),
          minFrontSpace: Number(minFrontSpace),
          manufacturer: Number(manufacturer),
          model: Number(model),
          remarks: remarks.trim(),
        }),
      },
    );

    yield put(frontClearanceActions.updateFrontClearanceSuccess());
    yield put(frontClearanceActions.getFrontClearanceList());
  } catch (error) {
    yield put(
      frontClearanceActions.updateFrontClearanceError((error as Error).message),
    );
  }
}

export function* handleDeleteFrontClearance(
  action: PayloadAction<FrontClearanceDeletePayload>,
) {
  const { superModel, year, cabinType, structureType } = action.payload;

  try {
    yield call(
      request,
      `${API_URL}/front-clearance?superModel=${superModel}&year=${year}&cabinType=${encodeURIComponent(
        cabinType,
      )}&structureType=${structureType}`,
      { method: "DELETE" },
    );

    yield put(frontClearanceActions.deleteFrontClearanceSuccess());
    yield put(frontClearanceActions.getFrontClearanceList());
  } catch (error) {
    yield put(
      frontClearanceActions.deleteFrontClearanceError((error as Error).message),
    );
  }
}

export function* handleGetFilterOptions(
  action: PayloadAction<FrontClearanceFilterEnum>,
) {
  const type = action.payload;

  try {
    const response: FrontClearanceFilterOptionsResponse = yield call(
      request,
      `${API_URL}/front-clearance/filter-options?type=${type}`,
      { method: "GET" },
    );

    const options = response?.data?.[type];
    yield put(
      frontClearanceActions.getFilterOptionsSuccess({
        type,
        data: Array.isArray(options) ? options : [],
      }),
    );
  } catch (error) {
    yield put(
      frontClearanceActions.getFilterOptionsError((error as Error).message),
    );
  }
}

export function* handleGetFormDropDownList(
  action: PayloadAction<FrontClearanceFormDropDownKey>,
) {
  const key = action.payload;
  if (fetchingFormDropDowns[key]) return;
  fetchingFormDropDowns[key] = true;

  try {
    const formDropDowns = yield select(selectFrontClearanceFormDropDowns);
    const { page, perPage } = formDropDowns[key];

    const url =
      key === "manufacturers"
        ? `${API_URL}/manufacturer?page=${page}&perPage=${perPage}`
        : `${API_URL}/super-model/list?page=${page}&perPage=${perPage}`;

    const response = yield call(request, url, { method: "GET" });
    const data = response?.data || [];

    const items =
      key === "manufacturers"
        ? data.map(
            (row: { menufecturerID: number; menufecturerName: string }) => ({
              id: row.menufecturerID,
              name: row.menufecturerName,
            }),
          )
        : data.map(
            (row: { superModelId: number; superModelName?: string }) => ({
              id: row.superModelId,
              name: row.superModelName || "",
            }),
          );

    const filtered = items.filter(
      (item: FrontClearanceFilterOption) => item.id && item.name,
    );
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { key, data: filtered, meta };

    if (page === 1) {
      yield put(frontClearanceActions.getFormDropDownListSuccess(payload));
    } else {
      yield put(frontClearanceActions.getFormDropDownNextPageSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const formDropDowns = yield select(selectFrontClearanceFormDropDowns);

    if (formDropDowns[key].page === 1) {
      yield put(
        frontClearanceActions.getFormDropDownListError({ key, message }),
      );
    } else {
      yield put(
        frontClearanceActions.getFormDropDownNextPageError({ key, message }),
      );
    }
  } finally {
    fetchingFormDropDowns[key] = false;
  }
}

export function* frontClearanceSaga() {
  yield takeLeading(
    frontClearanceActions.getFrontClearanceList.type,
    handleGetFrontClearanceList
  );
  yield takeLeading(
    frontClearanceActions.getNextPageItems.type,
    handleGetFrontClearanceList
  );
  yield takeLatest(
    frontClearanceActions.addFrontClearance.type,
    handleAddFrontClearance
  );
  yield takeLatest(
    frontClearanceActions.updateFrontClearance.type,
    handleUpdateFrontClearance,
  );
  yield takeLatest(
    frontClearanceActions.deleteFrontClearance.type,
    handleDeleteFrontClearance,
  );
  yield takeEvery(
    frontClearanceActions.getFilterOptions.type,
    handleGetFilterOptions,
  );
  yield takeEvery(
    frontClearanceActions.getFormDropDownList.type,
    handleGetFormDropDownList,
  );
  yield takeEvery(
    frontClearanceActions.getFormDropDownNextPage.type,
    handleGetFormDropDownList,
  );
}

export default frontClearanceSaga;
